import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import {
  Pill,
  Menu,
  CaretDown as CaretDownIcon,
  CheckboxChecked as CheckboxCheckedIcon,
  CheckboxIndeterminate as CheckboxIndeterminateIcon
  // @ts-ignore
} from '@netbasequid/core'

import { useDashboardViewLayout, useDisplayTheme, useSysTheme } from 'hooks/useUiState'
import { SystemThemeFull } from 'hooks/useUiState/types'
import { AutomationId } from 'utils/automationUtil'
import { setFilter } from 'features/dashboardInfo/dashboardInfoSlice'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  selectDashboardInfoStatus,
  selectMemorizedStateForV4
} from 'features/dashboardInfo/selectors'
import { setIsFilterDropdownOpen } from 'features/ui/uiSlice'
import { HeapEvent, HeapEventPageValue, heapTrack } from 'utils/heapUtils'

const StyledPill = styled(Pill)`
  cursor: pointer;
`

export const FilterPill = ({
  children,
  selected,
  onClick,
  restProps
}: {
  children: React.ReactNode
  selected?: boolean
  onClick?: () => void
  restProps?: Record<string, any>
}) => {
  const mode = useDisplayTheme()
  return (
    <StyledPill
      data-testid={AutomationId.sidePannel.filterPill}
      type="button"
      mode={mode}
      selected={selected}
      onClick={onClick}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...restProps}>
      {children}
    </StyledPill>
  )
}

const PillInnerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const DropdownPill = styled(Pill)`
  padding-right: 10px;
  cursor: pointer;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
`

const StyledLabel = styled.label`
  display: block;
  cursor: pointer;
  position: relative;
  padding: 5px 12px 5px 36px;
`

const StyledInput = styled.input`
  opacity: 0;
  position: absolute;
  left: 0;
`

const StyledCheckMark = styled.div<{
  $checked?: boolean
  $indeterminate?: boolean
  theme: SystemThemeFull
}>`
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  background-color: ${({ $checked, $indeterminate, theme }) =>
    $indeterminate || $checked ? theme.interaction.primary.default : 'unset'};
  border: ${({ $checked, $indeterminate, theme }) =>
    $indeterminate || $checked ? 'none' : `1px solid ${theme.interaction.border.default}`};
  border-radius: 2px;
  box-sizing: border-box;

  position: absolute;
  left: 12px;
  top: 8px;
`

const CheckBox = ({
  children,
  id,
  checked,
  onChange,
  indeterminate
}: {
  children?: React.ReactNode
  id: string
  checked?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  indeterminate?: boolean
}) => {
  const theme = useSysTheme() as SystemThemeFull
  return (
    <StyledLabel htmlFor={id}>
      <StyledInput
        type="checkbox"
        id={id}
        checked={indeterminate ? false : checked}
        onChange={onChange}
      />
      <StyledCheckMark theme={theme} $checked={checked} $indeterminate={indeterminate}>
        {indeterminate ? (
          <CheckboxIndeterminateIcon color={theme.dark.text.primary} />
        ) : (
          checked && <CheckboxCheckedIcon color={theme.dark.text.primary} />
        )}
      </StyledCheckMark>

      {children}
    </StyledLabel>
  )
}

const StyledMenuItem = styled(Menu.MenuItem)`
  padding: 0;
`

export const ReportPill = ({ page }: { page?: HeapEventPageValue }) => {
  const { isFilterDropdownOpen } = useDashboardViewLayout()
  const mode = useDisplayTheme()
  const { t } = useTranslation()
  const pillRef = useRef(null)
  const memorizedState = useAppSelector(selectMemorizedStateForV4)
  const { liveReportEnabled, scheduledReportEnabled } =
    memorizedState?.accessibleDashboards?.filter || {}
  const [report, setReport] = useState({
    selectAll: liveReportEnabled && scheduledReportEnabled,
    liveReport: liveReportEnabled,
    scheduledReport: scheduledReportEnabled
  })
  const dispatch = useAppDispatch()
  const status = useAppSelector(selectDashboardInfoStatus)

  const handleCheckChange = (e: any, reportKey: string) => {
    const isChecked = e.target.checked

    const tmpReport = {
      ...report,
      [reportKey]: isChecked
    }

    switch (reportKey) {
      case 'selectAll':
        tmpReport.selectAll = isChecked
        tmpReport.liveReport = isChecked
        tmpReport.scheduledReport = isChecked
        break
      case 'liveReport':
      case 'scheduledReport':
        tmpReport.selectAll = tmpReport.liveReport && tmpReport.scheduledReport
        break
      default:
        break
    }

    setReport(tmpReport)

    dispatch(
      setFilter({
        liveReportEnabled: tmpReport.liveReport,
        scheduledReportEnabled: tmpReport.scheduledReport
      })
    )
    heapTrack(HeapEvent.V4DashboardReportSelected, {
      onClickItem: reportKey,
      page,
      liveReportEnabled: tmpReport.liveReport,
      scheduledReportEnabled: tmpReport.scheduledReport
    })
  }

  const getReportKey = (): string => {
    if (report.liveReport && report.scheduledReport) return 'liveReportAndScheduledReport'
    if (report.liveReport) return 'liveReport'
    if (report.scheduledReport) return 'scheduledReport'
    return 'report'
  }

  const isIndeterminate = report.liveReport !== report.scheduledReport

  return (
    <>
      <DropdownPill
        ref={pillRef}
        mode={mode}
        type="button"
        disabled={status === 'loading'}
        selected={report.liveReport || report.scheduledReport}
        onClick={() => {
          dispatch(setIsFilterDropdownOpen(true))
        }}>
        <PillInnerContainer>
          {t('funcEnabled', { func: t(getReportKey()) })}
          <CaretDownIcon mode={mode} color="inherit" fontSize={16} />
        </PillInnerContainer>
      </DropdownPill>
      <>
        <Menu
          className="report-pill-menu"
          mode={mode}
          open={isFilterDropdownOpen}
          anchorEl={pillRef.current}
          onClose={() => {
            dispatch(setIsFilterDropdownOpen(false))
          }}
          disablePortal>
          <StyledMenuItem>
            <CheckBox
              id="selectAll"
              checked={report.selectAll}
              indeterminate={isIndeterminate}
              onChange={(e) => handleCheckChange(e, 'selectAll')}>
              {t('selectAll')}
            </CheckBox>
          </StyledMenuItem>
          <StyledMenuItem>
            <CheckBox
              id="liveReport"
              checked={report.liveReport}
              onChange={(e) => handleCheckChange(e, 'liveReport')}>
              {t('liveReport')}
            </CheckBox>
          </StyledMenuItem>
          <StyledMenuItem>
            <CheckBox
              id="scheduledReport"
              checked={report.scheduledReport}
              onChange={(e) => handleCheckChange(e, 'scheduledReport')}>
              {t('scheduledReport')}
            </CheckBox>
          </StyledMenuItem>
        </Menu>
      </>
    </>
  )
}
